'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link, { usePathname } from '@/core/i18n/navigation';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { BlockProps } from './types';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import { resolvePaginationAppearance, paginationItemStyle, paginationArrowStyle, paginationContainerStyle, paginationHoverCss, paginationScopeClass } from '../shared/paginationAppearance';

const DEMO_SUBCATEGORIES = [
  { id: 'demo-1', name: 'Podkategoria A', slug: 'a' },
  { id: 'demo-2', name: 'Podkategoria B', slug: 'b' },
  { id: 'demo-3', name: 'Podkategoria C', slug: 'c' },
  { id: 'demo-4', name: 'Podkategoria D', slug: 'd' },
  { id: 'demo-5', name: 'Podkategoria E', slug: 'e' },
  { id: 'demo-6', name: 'Podkategoria F', slug: 'f' },
  { id: 'demo-7', name: 'Podkategoria G', slug: 'g' },
  { id: 'demo-8', name: 'Podkategoria H', slug: 'h' },
  { id: 'demo-9', name: 'Podkategoria I', slug: 'i' },
  { id: 'demo-10', name: 'Podkategoria J', slug: 'j' },
  { id: 'demo-11', name: 'Podkategoria K', slug: 'k' },
  { id: 'demo-12', name: 'Podkategoria L', slug: 'l' },
];

export function SubcategoriesBlock({
  block,
  currentStyles: propCurrentStyles,
  isAdmin = false,
  category,
  viewport,
}: Partial<BlockProps> & { block: any }) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const settings = block.settings || {};
  const blockStyles = block.styles || {};
  const currentStyles = {
    ...blockStyles,
    ...(blockStyles.desktop || {}),
    ...(propCurrentStyles || {}),
  };

  const items: { id: string | number; name: string; slug: string }[] =
    category?.children?.length ? category.children
      : (isAdmin && !category) ? DEMO_SUBCATEGORIES
        : [];

  // Screen width detection for responsive page sizes
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [isExpanded, setIsExpanded] = useState(false);

  // Carousel ref
  const carouselRef = useRef<HTMLDivElement>(null);

  if (items.length === 0) return null;

  const categoryPath: string | null =
    category?.category_path && Array.isArray(category.category_path)
      ? category.category_path.map((c: any) => c.slug).join('/')
      : null;

  const showHeader = settings.showHeader ?? true;
  const rawHeaderText = settings.headerText;
  const headerText = (!rawHeaderText || rawHeaderText === 'Podkategorie') ? t('category.subcategories', 'Podkategorie') : rawHeaderText;
  const gapVal = currentStyles.gap !== undefined ? currentStyles.gap : settings.gap;
  const gap = gapVal !== undefined ? `${gapVal}px` : '12px';
  const justifyContent = currentStyles.justifyContent || 'center';

  const headerFontSize = currentStyles.headerFontSize ? `${currentStyles.headerFontSize}px` : '12px';
  const headerFontWeight = currentStyles.headerFontWeight || '900';
  const headerColor = currentStyles.headerColor || '#94a3b8'; // slate-400
  const headerFontFamily = currentStyles.headerFontFamily && currentStyles.headerFontFamily !== 'inherit'
    ? currentStyles.headerFontFamily
    : undefined;

  const pillBackgroundColor = currentStyles.pillBackgroundColor ?? '#f8fafc'; // slate-50
  const pillBorderColor = currentStyles.pillBorderColor ?? 'transparent';
  const pillBorderWidth = currentStyles.pillBorderWidth !== undefined ? `${currentStyles.pillBorderWidth}px` : '0px';
  const pillBorderRadius = currentStyles.pillBorderRadius !== undefined ? `${currentStyles.pillBorderRadius}px` : '16px';
  const pillTextColor = currentStyles.pillTextColor || '#475569'; // slate-600
  const pillFontSize = currentStyles.pillFontSize ? `${currentStyles.pillFontSize}px` : '14px';
  const pillFontWeight = currentStyles.pillFontWeight || '700';
  const pillFontFamily = currentStyles.pillFontFamily && currentStyles.pillFontFamily !== 'inherit'
    ? currentStyles.pillFontFamily
    : undefined;
  const pillPaddingX = currentStyles.pillPaddingX !== undefined ? `${currentStyles.pillPaddingX}px` : '24px';
  const pillPaddingY = currentStyles.pillPaddingY !== undefined ? `${currentStyles.pillPaddingY}px` : '12px';

  const pillHoverBackgroundColor = currentStyles.pillHoverBackgroundColor || '#ffffff';
  const pillHoverTextColor = currentStyles.pillHoverTextColor || 'var(--primary, #2563eb)';
  const pillHoverBorderColor = currentStyles.pillHoverBorderColor || 'transparent';
  const pillHoverShadow = currentStyles.pillHoverShadow ?? true;

  const pillActiveBackgroundColor = currentStyles.pillActiveBackgroundColor || pillHoverBackgroundColor;
  const pillActiveTextColor = currentStyles.pillActiveTextColor || pillHoverTextColor;
  const pillActiveBorderColor = currentStyles.pillActiveBorderColor || pillHoverBorderColor;

  // Pagination & Layout Settings
  const enablePagination = settings.enablePagination ?? true;
  const paginationMode = settings.paginationMode || (items.length > 8 ? 'pages' : 'none');
  const pageSizeDesktop = settings.pageSizeDesktop || 12;
  const pageSizeMobile = settings.pageSizeMobile || 6;
  // Admin canvas simulates viewport by scaling a real-width div — window.innerWidth below
  // keys off the real browser window, not that scaled width, so it never reacts to the
  // viewport toggle. Use the simulated `viewport` prop instead when in the admin editor.
  const pageSize = (isAdmin ? viewport === 'mobile' : isMobile) ? pageSizeMobile : pageSizeDesktop;
  const paginationAlign = settings.paginationAlign || justifyContent;

  const showMoreText = settings.showMoreText || t('category.show_more', 'Pokaż więcej');
  const showLessText = settings.showLessText || t('category.show_less', 'Zwiń');

  const safeId = String(block.id || 'subcat').replace(/[^a-zA-Z0-9_-]/g, '_');
  const containerClass = `subcat-block-${safeId}`;

  // Calculation for modes
  const isPaginated = enablePagination && paginationMode === 'pages';
  const pg = resolvePaginationAppearance(settings);
  const pgScope = paginationScopeClass(block.id);
  const pgHoverCss = paginationHoverCss(pgScope, pg);
  const isShowMore = enablePagination && paginationMode === 'show_more';
  const isCarousel = enablePagination && paginationMode === 'carousel';

  const totalPages = isPaginated ? Math.ceil(items.length / pageSize) : 1;

  let displayItems = items;
  if (isPaginated && items.length > pageSize) {
    const startIdx = (currentPage - 1) * pageSize;
    displayItems = items.slice(startIdx, startIdx + pageSize);
  } else if (isShowMore && !isExpanded && items.length > pageSize) {
    displayItems = items.slice(0, pageSize);
  }

  const scrollCarousel = (direction: 'left' | 'right') => {
    if (!carouselRef.current) return;
    const amount = direction === 'left' ? -280 : 280;
    carouselRef.current.scrollBy({ left: amount, behavior: 'smooth' });
  };

  return (
    <div className={cn("w-full relative z-10 block-inner-content", containerClass)}>
      <style>{`
        .${containerClass} .subcat-pill {
          background-color: ${pillBackgroundColor} !important;
          border-color: ${pillBorderColor} !important;
          color: ${pillTextColor} !important;
          border-width: ${pillBorderWidth} !important;
          border-style: solid !important;
          border-radius: ${pillBorderRadius} !important;
          font-size: ${pillFontSize} !important;
          font-weight: ${pillFontWeight} !important;
          padding: ${pillPaddingY} ${pillPaddingX} !important;
          transition: all 200ms ease-in-out !important;
          ${pillFontFamily ? `font-family: ${pillFontFamily} !important;` : ''}
        }
        .${containerClass} .subcat-pill:hover {
          background-color: ${pillHoverBackgroundColor} !important;
          border-color: ${pillHoverBorderColor} !important;
          color: ${pillHoverTextColor} !important;
          ${pillHoverShadow ? 'box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1) !important; transform: translateY(-2px) !important;' : ''}
        }
        .${containerClass} .subcat-pill.active {
          background-color: ${pillActiveBackgroundColor} !important;
          border-color: ${pillActiveBorderColor} !important;
          color: ${pillActiveTextColor} !important;
        }
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>

      {showHeader && (
        <div className="flex items-center gap-4 mb-6">
          <h2
            style={{
              fontSize: headerFontSize,
              fontWeight: headerFontWeight,
              color: headerColor,
              fontFamily: headerFontFamily,
            }}
            className="uppercase tracking-widest"
          >
            {headerText}
          </h2>
          <div className="h-px bg-slate-100 flex-1" />
        </div>
      )}

      {/* Render Mode: CAROUSEL */}
      {isCarousel ? (
        <div className="relative group/carousel">
          <button
            type="button"
            onClick={() => scrollCarousel('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-20 w-8 h-8 rounded-full bg-white shadow-md border border-slate-200 text-slate-700 flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 hover:scale-110 hover:bg-slate-50 transition-all duration-200 hidden sm:flex"
            aria-label="Previous"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div
            ref={carouselRef}
            className="flex items-center gap-3 overflow-x-auto no-scrollbar scroll-smooth py-2 px-1"
          >
            {items.map((sub) => {
              const href = categoryPath ? `/kategoria/${categoryPath}/${sub.slug}` : undefined;
              const isActive = !!href && pathname === href;

              return href ? (
                <Link
                  key={sub.id}
                  href={href}
                  className={cn('subcat-pill group', isActive && 'active')}
                  style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  <span>{sub.name}</span>
                </Link>
              ) : (
                <span
                  key={sub.id}
                  className={cn('subcat-pill inline-block cursor-default')}
                  style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  <span>{sub.name}</span>
                </span>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => scrollCarousel('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-20 w-8 h-8 rounded-full bg-white shadow-md border border-slate-200 text-slate-700 flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 hover:scale-110 hover:bg-slate-50 transition-all duration-200 hidden sm:flex"
            aria-label="Next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      ) : (
        /* Render Mode: GRID / PAGES / SHOW_MORE */
        <div>
          <div className="flex flex-wrap transition-all duration-300" style={{ gap, justifyContent }}>
            {displayItems.map((sub) => {
              const href = categoryPath ? `/kategoria/${categoryPath}/${sub.slug}` : undefined;
              const isActive = !!href && pathname === href;

              return href ? (
                <Link
                  key={sub.id}
                  href={href}
                  className={cn('subcat-pill group', isActive && 'active')}
                >
                  <span>{sub.name}</span>
                </Link>
              ) : (
                <span
                  key={sub.id}
                  className={cn('subcat-pill inline-block cursor-default')}
                >
                  <span>{sub.name}</span>
                </span>
              );
            })}
          </div>

          {/* Controls: PAGES */}
          {isPaginated && totalPages > 1 && (
            <div className={cn("mt-6 flex items-center gap-1.5", pgScope)} style={{ justifyContent: paginationAlign, ...paginationContainerStyle(pg) }}>
              {pgHoverCss && <style>{pgHoverCss}</style>}
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                data-pg-item
                style={paginationArrowStyle(pg)}
                className="w-8 h-8 rounded-xl border border-slate-200 bg-white flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white transition-all"
                title="Poprzednia strona"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
                const isCurrent = p === currentPage;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setCurrentPage(p)}
                    data-pg-item
                    data-pg-active={isCurrent ? 'true' : undefined}
                    aria-current={isCurrent ? 'page' : undefined}
                    style={paginationItemStyle(pg, isCurrent)}
                    className={cn(
                      'min-w-8 h-8 px-2 rounded-xl text-xs font-extrabold border transition-all',
                      isCurrent
                        ? 'bg-primary text-white border-primary shadow-sm scale-105'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    )}
                  >
                    {p}
                  </button>
                );
              })}

              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                data-pg-item
                style={paginationArrowStyle(pg)}
                className="w-8 h-8 rounded-xl border border-slate-200 bg-white flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white transition-all"
                title={t('category.next_page', 'Następna strona')}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Controls: SHOW MORE */}
          {isShowMore && items.length > pageSize && (
            <div className="mt-6 flex justify-center" style={{ justifyContent: paginationAlign }}>
              <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="px-5 py-2.5 rounded-full text-xs font-bold bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200/80 flex items-center gap-2 transition-all shadow-sm active:scale-95"
              >
                <span>
                  {isExpanded
                    ? showLessText
                    : `${showMoreText} (${items.length - pageSize})`}
                </span>
                {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


